import React, { useState, useEffect, useCallback } from 'react';
import { CircularProgress } from '@mui/material';
import sellerBalanceSettingsService, {
  SellerBalanceSettings,
  SellerBalanceStats
} from '../services/sellerBalanceSettingsService';
import './FinancialPages.css';

const SellerBalanceSettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SellerBalanceSettings | null>(null);
  const [stats, setStats] = useState<SellerBalanceStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Test dialog
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testAmount, setTestAmount] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [testLoading, setTestLoading] = useState(false);

  // Action states
  const [recalculateLoading, setRecalculateLoading] = useState(false);
  const [checkExpiredLoading, setCheckExpiredLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsData, statsData] = await Promise.all([
        sellerBalanceSettingsService.getSettings(),
        sellerBalanceSettingsService.getStats()
      ]);
      setSettings(settingsData);
      setStats(statsData);
    } catch (err) {
      setError('خطا در بارگذاری تنظیمات');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      await sellerBalanceSettingsService.updateSettings(settings);
      setSuccess('تنظیمات با موفقیت ذخیره شد');
    } catch (err) {
      setError('خطا در ذخیره تنظیمات');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculateBalances = async () => {
    setRecalculateLoading(true);
    setError(null);
    try {
      const result = await sellerBalanceSettingsService.recalculateAllBalances();
      setSuccess(`${result.affected} فروشنده بازمحاسبه شد`);
      loadData();
    } catch (err) {
      setError('خطا در بازمحاسبه موجودی‌ها');
    } finally {
      setRecalculateLoading(false);
    }
  };

  const handleCheckExpired = async () => {
    setCheckExpiredLoading(true);
    setError(null);
    try {
      const result = await sellerBalanceSettingsService.checkExpiredWithdrawals();
      setSuccess(`${result.rejectedCount} درخواست منقضی رد شد`);
      loadData();
    } catch (err) {
      setError('خطا در بررسی درخواست‌های منقضی');
    } finally {
      setCheckExpiredLoading(false);
    }
  };

  const handleTestSettings = async () => {
    if (!testAmount) return;
    setTestLoading(true);
    try {
      const result = await sellerBalanceSettingsService.testSettings({ amount: Number(testAmount) });
      setTestResult(result);
    } catch (err) {
      setTestResult({ isValid: false, message: 'خطا در تست' });
    } finally {
      setTestLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="financial-page">
        <div className="loading-overlay">
          <CircularProgress />
          <p>در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="financial-page">
      <div className="page-header">
        <h1 className="page-title">
          ⚙️ تنظیمات مالی فروشندگان
        </h1>
        <button className="refresh-btn" onClick={loadData}>
          🔄 بروزرسانی
        </button>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {success && (
        <div className="alert success" style={{ background: '#dcfce7', border: '1px solid #86efac', color: '#15803d' }}>
          {success}
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }} onClick={() => setSuccess(null)}>✕</button>
        </div>
      )}

      {/* Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon primary">👥</div>
          <div className="stat-content">
            <div className="stat-value">{(stats?.totalSellers || 0).toLocaleString('fa-IR')}</div>
            <div className="stat-label">تعداد فروشندگان</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon completed">💰</div>
          <div className="stat-content">
            <div className="stat-value">{(stats?.totalBalance || 0).toLocaleString('fa-IR')}</div>
            <div className="stat-label">موجودی کل</div>
            <div className="stat-extra">تومان</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon pending">⏳</div>
          <div className="stat-content">
            <div className="stat-value">{(stats?.pendingWithdrawals || 0).toLocaleString('fa-IR')}</div>
            <div className="stat-label">در انتظار برداشت</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon info">✅</div>
          <div className="stat-content">
            <div className="stat-value">{(stats?.completedWithdrawals || 0).toLocaleString('fa-IR')}</div>
            <div className="stat-label">برداشت تکمیل شده</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon warning">📊</div>
          <div className="stat-content">
            <div className="stat-value">{(stats?.averageBalance || 0).toLocaleString('fa-IR')}</div>
            <div className="stat-label">میانگین موجودی</div>
            <div className="stat-extra">تومان</div>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      {settings && (
        <div className="table-container" style={{ padding: '24px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>⚙️ تنظیمات</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>
            <div className="form-group">
              <label>📊 نرخ کمیسیون (%)</label>
              <input
                type="number"
                className="form-input"
                value={settings.commissionRate}
                onChange={(e) => setSettings({ ...settings, commissionRate: Number(e.target.value) })}
                step="0.1"
                min="0"
                max="100"
              />
            </div>

            <div className="form-group">
              <label>💵 حداقل مبلغ برداشت (تومان)</label>
              <input
                type="number"
                className="form-input"
                value={settings.minWithdrawalAmount}
                onChange={(e) => setSettings({ ...settings, minWithdrawalAmount: Number(e.target.value) })}
              />
            </div>

            <div className="form-group">
              <label>💰 حداکثر مبلغ برداشت (تومان)</label>
              <input
                type="number"
                className="form-input"
                value={settings.maxWithdrawalAmount}
                onChange={(e) => setSettings({ ...settings, maxWithdrawalAmount: Number(e.target.value) })}
              />
            </div>

            <div className="form-group">
              <label>⏱️ روزهای انقضای درخواست</label>
              <input
                type="number"
                className="form-input"
                value={settings.withdrawalExpirationDays}
                onChange={(e) => setSettings({ ...settings, withdrawalExpirationDays: Number(e.target.value) })}
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.autoRejectExpiredWithdrawals}
                  onChange={(e) => setSettings({ ...settings, autoRejectExpiredWithdrawals: e.target.checked })}
                  style={{ marginLeft: '8px' }}
                />
                ❌ رد خودکار درخواست‌های منقضی
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.requireBankAccountVerification}
                  onChange={(e) => setSettings({ ...settings, requireBankAccountVerification: e.target.checked })}
                  style={{ marginLeft: '8px' }}
                />
                ✔️ نیاز به تایید حساب بانکی
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="btn-primary"
              onClick={handleSaveSettings}
              disabled={saving}
            >
              {saving ? <CircularProgress size={16} style={{ color: '#fff' }} /> : '💾'} ذخیره تنظیمات
            </button>
            <button
              className="btn-secondary"
              onClick={() => setTestDialogOpen(true)}
            >
              🧪 تست تنظیمات
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="table-container" style={{ padding: '24px' }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>🔧 عملیات</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
          <button
            className="btn-primary"
            onClick={handleRecalculateBalances}
            disabled={recalculateLoading}
            style={{ padding: '16px', fontSize: '14px', flexDirection: 'column', gap: '8px' }}
          >
            {recalculateLoading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : <span style={{ fontSize: '24px' }}>🔄</span>}
            بازمحاسبه همه موجودی‌ها
          </button>

          <button
            className="btn-secondary"
            onClick={handleCheckExpired}
            disabled={checkExpiredLoading}
            style={{ padding: '16px', fontSize: '14px', flexDirection: 'column', gap: '8px', border: '1px solid #f59e0b', color: '#f59e0b' }}
          >
            {checkExpiredLoading ? <CircularProgress size={20} style={{ color: '#f59e0b' }} /> : <span style={{ fontSize: '24px' }}>⏰</span>}
            بررسی درخواست‌های منقضی
          </button>
        </div>
      </div>

      {/* Test Dialog */}
      {testDialogOpen && (
        <div className="dialog-overlay" onClick={() => setTestDialogOpen(false)}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>🧪 تست تنظیمات</h3>
              <button className="dialog-close-btn" onClick={() => setTestDialogOpen(false)}>✕</button>
            </div>
            <div className="dialog-body">
              <div className="form-group">
                <label>مبلغ تست (تومان)</label>
                <input
                  type="number"
                  className="form-input"
                  value={testAmount}
                  onChange={(e) => setTestAmount(e.target.value)}
                  placeholder="مبلغ را وارد کنید"
                />
              </div>

              {testResult && (
                <div className={`alert-box ${testResult.isValid ? 'info' : 'error'}`}>
                  {testResult.isValid ? (
                    <>
                      <strong>✅ تست موفقیت‌آمیز</strong>
                      <p>کمیسیون تخمینی: {testResult.estimatedCommission?.toLocaleString('fa-IR')} تومان</p>
                      <p>مبلغ خالص: {testResult.estimatedNetAmount?.toLocaleString('fa-IR')} تومان</p>
                    </>
                  ) : (
                    <>
                      <strong>❌ خطا</strong>
                      <p>{testResult.message}</p>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="dialog-footer">
              <button className="btn-secondary" onClick={() => setTestDialogOpen(false)}>
                بستن
              </button>
              <button
                className="btn-primary"
                onClick={handleTestSettings}
                disabled={testLoading || !testAmount}
              >
                {testLoading ? <CircularProgress size={16} style={{ color: '#fff' }} /> : '🧪'} تست
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerBalanceSettingsPage;
